import { MyGrowthQuery } from 'core';

type GrowthNode = NonNullable<MyGrowthQuery['myGrowth']>;

export interface IGrowthAchievement {
  /** 'percent' | 'count' — «70 из 100» и «70%» читаются по-разному. */
  progressKind: string;
  code: string;
  title: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  earned: boolean;
  earnedAt: string | null;
  current: number;
  target: number;
  /** Правило недостижимо вне карьерного центра — показываем с замком. */
  locked: boolean;
  /** Секретная и ещё не полученная: силуэт без названия. */
  hidden: boolean;
}

export interface IGrowthLevel {
  index: number;
  title: string;
  /** Сколько очков нужно набрать, чтобы ступень открылась. */
  threshold: number;
  reached: boolean;
}

export interface IGrowthOverview {
  xp: number;
  level: number;
  levelTitle: string;
  /** `null` на последнем уровне — «дальше некуда», а не «ноль до следующего». */
  nextLevelXp: number | null;
  /** Порог ТЕКУЩЕГО уровня — от него считается полоса, а не от нуля. */
  levelFloorXp: number;
  /** Сколько медалей доступно этому студенту и сколько заперто центром. */
  availableTotal: number;
  lockedTotal: number;
  isCareerMember: boolean;
  achievements: IGrowthAchievement[];
  /** Сколько очков до следующего уровня; 0 — уровень последний. */
  toNextLevel: number;
  /** Вся лестница, а не только текущая ступень: ответ на «а что дальше». */
  levels: IGrowthLevel[];
}

/** ТЗ 0039 шаг 4. Приводит ответ к виду, удобному экрану. */
export const transformGrowth = (node: GrowthNode | null | undefined): IGrowthOverview | null => {
  if (!node) return null;

  const nextLevelXp = node.nextLevelXp ?? null;

  return {
    xp: node.xp,
    level: node.level,
    levelTitle: node.levelTitle,
    nextLevelXp,
    levelFloorXp: node.levelFloorXp,
    availableTotal: node.availableTotal,
    lockedTotal: node.lockedTotal,
    isCareerMember: node.isCareerMember,
    // Считаем ЗДЕСЬ, а не в вёрстке: `nextLevelXp - xp` на последнем уровне
    // даёт NaN, и полоса прогресса молча исчезает.
    toNextLevel: nextLevelXp === null ? 0 : Math.max(0, nextLevelXp - node.xp),
    levels: node.levels.map((row) => ({
      index: row.index,
      title: row.title,
      threshold: row.threshold,
      reached: row.reached,
    })),
    achievements: node.achievements.map((row) => ({
      code: row.code,
      title: row.title,
      description: row.description,
      icon: row.icon,
      category: row.category,
      points: row.points,
      earned: row.earned,
      earnedAt: row.earnedAt ?? null,
      current: row.current,
      target: row.target,
      locked: row.locked,
      hidden: row.hidden,
      progressKind: row.progressKind,
    })),
  };
};
